/**
 * 주간 보고 마감본. 같은 주는 한 번만 얼리고, 이후 일정·기록을 고쳐도 본문은 그대로다.
 * 잘못된 마감본은 정정본(revision)으로 다시 열어 같은 기준일의 다음 버전으로 마감한다.
 */
import { isISODate } from "@/engine/date";
import type { Project, WeeklyReportSnapshot } from "@/engine/types";
import type { WeeklyV2WeekSnapshot } from "./weekly-report-v2-data";

export function stableWeekId(weekId: string, statusDate: string): string {
  if (weekId === "current" || isISODate(weekId)) return `live:${statusDate}`;
  return weekId;
}

export function formatReportNo(statusDate: string, version = 1): string {
  const base = `WR-${statusDate.replaceAll("-", "")}`;
  return version > 1 ? `${base}-v${version}` : base;
}

export function findWeeklySnapshotsForDate(
  project: Pick<Project, "weeklySnapshots">,
  statusDate: string,
): WeeklyReportSnapshot[] {
  const key = `live:${statusDate}`;
  return (project.weeklySnapshots ?? [])
    .filter((snapshot) => snapshot.statusDate === statusDate || snapshot.weekId === key)
    .sort((a, b) => (a.version ?? 1) - (b.version ?? 1));
}

/**
 * 기준일(또는 weekId)의 마감본을 찾는다. 정정본이 여러 개 있으면 마지막(최신 버전) 마감본을 돌려준다.
 */
export function findWeeklySnapshot(
  project: Pick<Project, "weeklySnapshots">,
  weekId: string,
  statusDate: string,
  version?: number,
): WeeklyReportSnapshot | undefined {
  const key = stableWeekId(weekId, statusDate);
  const matches = (project.weeklySnapshots ?? []).filter(
    (snapshot) => snapshot.weekId === key || snapshot.statusDate === statusDate,
  );
  if (matches.length === 0) return undefined;
  if (version != null) {
    return matches.find((s) => (s.version ?? 1) === version) ?? matches[matches.length - 1];
  }
  return matches.reduce((latest, cur) => ((cur.version ?? 1) >= (latest.version ?? 1) ? cur : latest), matches[0]!);
}

export function readFrozenReport(snapshot: WeeklyReportSnapshot | undefined): WeeklyV2WeekSnapshot | null {
  const report = snapshot?.document?.report;
  if (!report || typeof report.projectName !== "string" || typeof report.weekId !== "string" || !Array.isArray(report.wbs)) return null;
  return report as unknown as WeeklyV2WeekSnapshot;
}

export function freezeWeeklyReport(
  project: Project,
  input: {
    id: string;
    weekId: string;
    statusDate: string;
    periodLabel: string;
    report: WeeklyV2WeekSnapshot;
    closedAt: string;
    /** 정정본 마감이면 true. 같은 기준일의 다음 버전으로 저장하고 이전 마감본은 남긴다. */
    revision?: boolean;
  },
): { project: Project; stored: boolean; snapshot: WeeklyReportSnapshot } {
  const baseKey = stableWeekId(input.weekId, input.statusDate);
  const existingList = (project.weeklySnapshots ?? []).filter(
    (snapshot) => snapshot.weekId === baseKey || snapshot.statusDate === input.statusDate,
  );
  if (existingList.length > 0 && !input.revision) {
    const latest = existingList.reduce((acc, cur) => ((cur.version ?? 1) >= (acc.version ?? 1) ? cur : acc), existingList[0]!);
    return { project, stored: false, snapshot: latest };
  }

  const maxVersion = existingList.reduce((max, cur) => Math.max(max, cur.version ?? 1), 0);
  const version = input.revision ? Math.max(2, maxVersion + 1) : 1;
  const weekId = version > 1 ? `${baseKey}:v${version}` : baseKey;
  const clonedReport = structuredClone(input.report);
  clonedReport.reportNo = formatReportNo(input.statusDate, version);
  clonedReport.versionNumber = version;
  clonedReport.closedAt = input.closedAt;

  const snapshot: WeeklyReportSnapshot = {
    id: input.id,
    weekId,
    statusDate: input.statusDate,
    periodLabel: input.periodLabel,
    closedAt: input.closedAt,
    version,
    document: {
      capturedAt: input.closedAt,
      weekId,
      statusDate: input.statusDate,
      report: clonedReport as unknown as Record<string, unknown>,
    },
  };
  return {
    project: { ...project, weeklySnapshots: [...(project.weeklySnapshots ?? []), snapshot] },
    stored: true,
    snapshot,
  };
}
